import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const CratePanel = ({ 
  isOpen, 
  onClose, 
  onAddTrack, 
  selectedTrackId = null,
  onCrateSelect = null 
}) => {
  const [crates, setCrates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCrateName, setNewCrateName] = useState('');
  const [newCrateDescription, setNewCrateDescription] = useState('');
  const [expandedCrate, setExpandedCrate] = useState(null);
  const [crateDetails, setCrateDetails] = useState({});
  const [downloadingCrate, setDownloadingCrate] = useState(null);

  useEffect(() => {
    if (isOpen) fetchCrates();
  }, [isOpen]);

  const fetchCrates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/crates`, { withCredentials: true });
      setCrates(response.data);
    } catch (error) {
      console.error('Error fetching crates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCrateDetails = async (crateId) => {
    try {
      const response = await api.get(`/crates/${crateId}`, { withCredentials: true });
      setCrateDetails(prev => ({ ...prev, [crateId]: response.data }));
    } catch (error) {
      console.error('Error fetching crate details:', error);
    }
  };

  const handleCreateCrate = async (e) => {
    e.preventDefault();
    if (!newCrateName.trim()) return;
    try {
      await api.post(`/crates`, { name: newCrateName, description: newCrateDescription }, { withCredentials: true });
      setNewCrateName('');
      setNewCrateDescription('');
      setShowCreateForm(false);
      fetchCrates();
    } catch (error) {
      alert('Error al crear la crate');
    }
  };

  const handleDeleteCrate = async (crateId) => {
    if (!window.confirm('Eliminar esta crate?')) return;
    try {
      await api.delete(`/crates/${crateId}`, { withCredentials: true });
      fetchCrates();
    } catch (error) {
      alert('Error al eliminar la crate');
    }
  };

  const handleAddTrackToCrate = async (crateId) => {
    if (!selectedTrackId) return;
    try {
      await api.post(`/crates/${crateId}/tracks`, { track_id: selectedTrackId }, { withCredentials: true });
      fetchCrates();
      if (expandedCrate === crateId) fetchCrateDetails(crateId);
      if (onAddTrack) onAddTrack(crateId);
    } catch (error) {
      alert('Error al agregar el track');
    }
  };

  const handleRemoveTrackFromCrate = async (crateId, trackId) => {
    try {
      await api.delete(`/crates/${crateId}/tracks/${trackId}`, { withCredentials: true });
      fetchCrates();
      fetchCrateDetails(crateId);
    } catch (error) {
      alert('Error al remover el track');
    }
  };

  const handleDownloadCrate = async (crate) => {
    if (crate.track_count === 0) { alert('La crate esta vacia'); return; }
    try {
      setDownloadingCrate(crate.crate_id);
      const response = await api.post(`/crates/${crate.crate_id}/download`, {}, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WTPL_${crate.name.replace(/\s+/g, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error al descargar la crate');
    } finally {
      setDownloadingCrate(null);
    }
  };

  const toggleExpandCrate = (crateId) => {
    if (expandedCrate === crateId) {
      setExpandedCrate(null);
    } else {
      setExpandedCrate(crateId);
      if (!crateDetails[crateId]) fetchCrateDetails(crateId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="bg-[#0A0A0A] rounded-2xl border border-[#1E1E1E] w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#1E1E1E] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#00D9FF]/10 border border-[#00D9FF]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#00D9FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Mis Crates</h2>
              <p className="text-xs text-[#555]">{crates.length} {crates.length === 1 ? 'crate' : 'crates'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-crate-panel"
            className="w-8 h-8 rounded-lg bg-[#141414] border border-[#262626] flex items-center justify-center text-[#555] hover:text-white hover:border-[#444] transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Add Track Notice */}
          {selectedTrackId && (
            <div className="bg-[#00D9FF]/5 border border-[#00D9FF]/20 rounded-xl p-3 flex items-center gap-2.5">
              <svg className="w-4 h-4 text-[#00D9FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p className="text-[#00D9FF] text-sm">Selecciona una crate para agregar el track</p>
            </div>
          )}

          {/* Create New Crate */}
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              data-testid="create-crate-btn"
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#141414] border border-[#262626] rounded-xl text-sm text-[#888] hover:text-[#00D9FF] hover:border-[#00D9FF]/30 hover:bg-[#00D9FF]/5 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
              </svg>
              Crear Nueva Crate
            </button>
          ) : (
            <form onSubmit={handleCreateCrate} className="bg-[#141414] border border-[#00D9FF]/30 rounded-xl p-4 space-y-3">
              <input
                type="text"
                placeholder="Nombre de la crate..."
                value={newCrateName}
                onChange={(e) => setNewCrateName(e.target.value)}
                autoFocus
                data-testid="crate-name-input"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#00D9FF]/50"
              />
              <input
                type="text"
                placeholder="Descripcion (opcional)"
                value={newCrateDescription}
                onChange={(e) => setNewCrateDescription(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#00D9FF]/50"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  data-testid="save-crate-btn"
                  className="flex-1 py-2.5 bg-[#00D9FF] hover:bg-[#00C4E8] text-black font-semibold text-sm rounded-lg transition-colors"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateForm(false); setNewCrateName(''); setNewCrateDescription(''); }}
                  className="flex-1 py-2.5 bg-[#1E1E1E] hover:bg-[#262626] text-[#888] text-sm rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Crates List */}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 rounded-full border-2 border-[#262626] border-t-[#00D9FF] animate-spin"></div>
            </div>
          ) : crates.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-[#141414] border border-[#262626] flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-[#333]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </div>
              <p className="text-[#555] text-sm">No tienes crates aun</p>
              <p className="text-[#333] text-xs mt-1">Crea una para organizar tus tracks</p>
            </div>
          ) : (
            <div className="space-y-2">
              {crates.map((crate) => (
                <div
                  key={crate.crate_id}
                  data-testid={`crate-${crate.crate_id}`}
                  className={`bg-[#141414] border rounded-xl transition-all ${
                    expandedCrate === crate.crate_id ? 'border-[#00D9FF]/40' : 'border-[#1E1E1E] hover:border-[#333]'
                  }`}
                >
                  <div className="flex items-center gap-3 p-3.5">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      expandedCrate === crate.crate_id ? 'bg-[#00D9FF]/10 border border-[#00D9FF]/20' : 'bg-[#1E1E1E]'
                    }`}>
                      <svg className={`w-5 h-5 ${expandedCrate === crate.crate_id ? 'text-[#00D9FF]' : 'text-[#555]'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                      </svg>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpandCrate(crate.crate_id)}>
                      <h3 className="font-semibold text-white text-sm truncate">{crate.name}</h3>
                      <p className="text-xs text-[#555]">
                        {crate.track_count} {crate.track_count === 1 ? 'track' : 'tracks'}
                        {crate.description && <span className="text-[#333]"> &middot; {crate.description}</span>}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {selectedTrackId ? (
                        <button
                          onClick={() => handleAddTrackToCrate(crate.crate_id)}
                          className="px-3 py-1.5 bg-[#00D9FF] hover:bg-[#00C4E8] text-black text-xs font-bold rounded-lg transition-colors"
                        >
                          + Agregar
                        </button>
                      ) : (
                        <>
                          {/* Download */}
                          <button
                            onClick={() => handleDownloadCrate(crate)}
                            disabled={downloadingCrate === crate.crate_id || crate.track_count === 0}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#555] hover:text-[#00D9FF] hover:bg-[#00D9FF]/10 transition-all disabled:opacity-30"
                            title="Descargar ZIP"
                          >
                            {downloadingCrate === crate.crate_id ? (
                              <div className="w-4 h-4 rounded-full border-2 border-[#262626] border-t-[#00D9FF] animate-spin"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                              </svg>
                            )}
                          </button>
                          {/* Expand */}
                          <button
                            onClick={() => toggleExpandCrate(crate.crate_id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#555] hover:text-white hover:bg-[#1E1E1E] transition-all"
                          >
                            <svg className={`w-4 h-4 transition-transform ${expandedCrate === crate.crate_id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteCrate(crate.crate_id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#555] hover:text-[#00D9FF] hover:bg-[#00D9FF]/10 transition-all"
                            title="Eliminar crate"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded Tracks */}
                  <AnimatePresence>
                    {expandedCrate === crate.crate_id && crateDetails[crate.crate_id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3.5 pb-3.5 pt-1">
                          <div className="border-t border-[#1E1E1E] pt-3">
                            {crateDetails[crate.crate_id].tracks?.length > 0 ? (
                              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                                {crateDetails[crate.crate_id].tracks.map((track) => (
                                  <div 
                                    key={track.track_id}
                                    className="flex items-center gap-2.5 px-3 py-2 bg-[#0A0A0A] rounded-lg hover:bg-[#111] transition-colors group"
                                  >
                                    <svg className="w-3.5 h-3.5 text-[#00D9FF]/50 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-xs font-medium truncate">{track.title}</p>
                                      <p className="text-[#444] text-[10px] truncate">{track.artist}</p>
                                    </div>
                                    {track.version && (
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${
                                        track.version === 'Clean' ? 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/20'
                                          : track.version === 'Dirty' ? 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/20'
                                          : track.version === 'Remix' ? 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/20'
                                          : 'bg-[#262626] text-[#666] border-[#333]'
                                      }`}>
                                        {track.version}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleRemoveTrackFromCrate(crate.crate_id, track.track_id)}
                                      className="w-6 h-6 rounded flex items-center justify-center text-[#333] hover:text-[#00D9FF] opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[#333] text-xs text-center py-4">Crate vacia</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CratePanel;
